import { forwardRef, ReactElement, ReactNode } from 'react';

import styled from 'styled-components';

import { Avatar, AvatarProps } from '@components/avatar';
import { IconButton as IB, IconButtonProps as IBProps } from '@components/icon-button';
import { IconProps } from '@components/icons';
import { StatusIndicatorGroup } from '@components/status-indicator';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { transitionStandard } from '@utils/transitions';

type ButtonSizeProps = {
  button: string;
  icon: string;
};

export const size: ButtonSizeProps = {
  button: pxToRem(32),
  icon: pxToRem(16),
};

export type Assignee = {
  id: string;
  name: string;
  picture: string;
};

export type AssignButtonProps = Pick<AvatarProps, 'color' | 'label' | 'noAbbreviate'> & {
  assignee: Assignee;
};

export const AssignButton = forwardRef<HTMLDivElement, AssignButtonProps>((props, ref) => {
  const { assignee, noAbbreviate, color = 'purple', ...rest } = props;
  return (
    <StyledAvatar
      ref={ref}
      name={assignee.name}
      src={assignee.picture}
      size="lg"
      color={color}
      border
      noAbbreviate={noAbbreviate}
      {...rest}
    />
  );
});

export type IconButtonProps = Pick<IBProps, 'tooltip'> & {
  icon: ReactElement<IconProps>;
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>((props, ref) => {
  const { icon, ...rest } = props;

  return (
    <StyledIconButton
      ref={ref}
      aria-label={rest.tooltip}
      {...rest}
      icon={<icon.type {...icon.props} size={size.icon} />}
      rounded
      variant="white-purple"
      size="md"
    />
  );
});

// Neutral chips ("+N") react like the neutral status indicator beside them.
const StyledAvatar = styled(Avatar)({
  transition: transitionStandard('background-color', 'color'),
  '&[data-color="theme"]:hover': {
    backgroundColor: theme.color.backgroundLightDarkHover,
    color: theme.color.buttonIconPositiveHover,
  },
});

const StyledIconButton = styled(IB)({
  borderWidth: pxToRem(2),
  borderStyle: 'solid',
  borderColor: theme.color.border20,
  '&:hover': {
    backgroundColor: theme.color.backgroundLightDarkHover,
  },
});

export type AssignListProps = {
  children: ReactNode;
};

export const AssignList = (props: AssignListProps) => (
  <StatusIndicatorGroup className="assignee-list" {...props} />
);

AssignList.Avatar = AssignButton;
AssignList.Icon = IconButton;
